import {
  Button,
  DatePicker,
  InputNumber,
  Select,
  Form,
  Radio,
  Flex,
  Input,
  Checkbox,
  Steps,
  Tag,
  Divider,
  Row,
  Col,
  Modal,
  Result,
} from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import TextArea from "antd/es/input/TextArea";
import Navbar from "../../components/navbar";
import Footer from "../../components/footer";
import { useLocation, useNavigate } from "react-router-dom";
import { BookDescription, BookTitle, ContainerBook, FormContainer } from "../../styled-components/book";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { RETRIEVE_MENUS } from "../../apollo/query/menu.query";
import { RETRIEVE_AVAILABILITY_BOOK, RETRIEVE_TIME_SLOTS } from "../../apollo/query/time-slot.query";
import { NotificationContext } from "../../context/notification-provider";
import { INSERT_RESERVATION } from "../../apollo/mutation/book.mutation";

const { Option } = Select;

const Book = () => {
    /** Form utils */
    const [form] = Form.useForm();
    /** Translation utils */
    const { i18n, t } = useTranslation();
    /** Navigation utilities */
    const navigate = useNavigate();
    /** Number of guests information */
    const [guestsNumber, setGuestsNumber] = useState<number>(0);
    /** State to manage the decision about the menu for the users */
    const [wantMenu, setWantMenu] = useState<boolean>(false);
    /** Parameter utilities */
    const { state: { nIDRestaurant } } = useLocation();
    /** Manage to retrieve all the available menus of the restaurant */
    const { data } = useQuery(RETRIEVE_MENUS, { variables: { nIDRestaurant: nIDRestaurant } });
    /** Manage to retrieve the time slots available */
    const { data: times } = useQuery(RETRIEVE_TIME_SLOTS, { variables: { nIDRestaurant: nIDRestaurant } });
    /** Manage which step of the form the user is in */
    const [currentTab, setCurrentTab] = useState<number>(0);
    /** The time slot selected */
    const [timeSlot, setTimeSlot] = useState<any>(null);
    /** Manage to check if a new booking on a time slot is available */
    const [ checkAvailability ] = useLazyQuery(RETRIEVE_AVAILABILITY_BOOK, { fetchPolicy: 'no-cache' }  );
    /** Notification utilities */
    const { showNotification } = useContext(NotificationContext);
    /** State to manage the reservation information */
    const [reservation, setReservation] = useState<any>({});
    /** Manage to insert a new reservation */
    const [ insertReservation, { data: bookingResponse } ] = useMutation(INSERT_RESERVATION);
    /** State to manage the visibility of confirmation message */
    const [confirmation, setConfirmation] = useState<boolean>(false);

    const onClose = () => {
        form.resetFields();
        setCurrentTab(0);
        setGuestsNumber(0);
        setWantMenu(false);
        setTimeSlot(null);
        setReservation({});
        setConfirmation(false);
    }
    /** Manage to show the response alert when updation or insertion is done */
    useEffect((): void => {
        // eslint-disable-next-line eqeqeq
        if( bookingResponse?.code == 200 ) {
            setConfirmation(true);
        }
    }, [bookingResponse]);

    /** Handle the change values of the form */
    const handleValuesChange = (changedValues: any) => {
        if ('nGuests' in changedValues) {
            setGuestsNumber(changedValues.nGuests);
        } else if ('bMenu' in changedValues) {
            setWantMenu(changedValues.bMenu);
        }
    };

    /** Check when to disable the dates */
    const disabledDate = (current: Dayjs) => {
        return current && current <= dayjs().startOf("day");
    };

    /** Manage to submit the formulary */
    const handleSubmit = async (values: any) => {
        if( currentTab === 0 ) {
            setReservation((prevState: any) => ({ ...prevState, ...values }));
            const { data } = await checkAvailability({ variables: {
                timeSlot: values.nIDTimeSlot,
                date: dayjs(values.date).format("YYYY-MM-DD")
            } });

            // eslint-disable-next-line eqeqeq
            if( data?.code == 200 ) {
                setCurrentTab(currentTab + 1);
            } else {
                showNotification({
                    timeout: 5000,
                    severity: 'warning',
                    title: 'Ja no s\'accepten mes reserves en la franja seleccionada del dia que vol reservar.' });
            }
            return;
        }
        if( currentTab !== 2 ) {
            setCurrentTab(currentTab + 1);
            setReservation((prevState: any) => ({ ...prevState, ...values }));
            return;
        }

        const { date, ...rest } = reservation;
        const bookInfo = {
            ...rest,
            nIDRestaurant: nIDRestaurant,
            dDate: dayjs(date).format("YYYY-MM-DD"),
            bAcceptConditions: values.bAcceptConditions,
            bMenu: rest.bMenu ?? false,
            nIDMenu: rest.nIDMenu ?? null,
            sSetting: rest.sSetting ?? null,
            sDescription: rest.sDescription ?? null,
            nStatus: 0
        };
        insertReservation({ variables: { book: bookInfo } });
    };

    /** Manage to redirect to legal page */
    const redirectToLegal = (): void => {
        navigate('/legal', { state: { nIDRestaurant: nIDRestaurant }} );
    }

    /** Manage the change of time slots and the creation of the available times */
    const onTimeSlot = (timeSlot: number): void => {
        // eslint-disable-next-line eqeqeq
        const slot = times?.data?.find((x: any) => x.nID == timeSlot);
        const intervals = [];

        let start = new Date(`1970-01-01T${slot.tStart}:00`);
        const end = new Date(`1970-01-01T${slot.tEnd}:00`);

        while (start < end) {
            let next = new Date(start.getTime() + 15 * 60000);
            if (next > end) break;
            intervals.push(start.toTimeString().slice(0, 5));
            start = next;
        }
        form.resetFields(['time']);
        setTimeSlot(intervals);
    }
    
    return (
        <div id="container" style={{ height: '100%', width: '100%', backgroundColor: '#FAFAFA' }}>
            <Navbar nIDRestaurant={nIDRestaurant} />
            <div style={{ position: 'relative', height: 400 }}>
                <div style={{ position: 'absolute', inset: 0, height: '100%' }}>
                    <img
                        src={`https://api.ams-carriot-restaurant.com/restaurant-image/${nIDRestaurant}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        alt="Restaurant Interior"
                    />
                    <div style={{ position: 'absolute', inset: 0, backgroundColor: 'black', opacity: .6 }} />
                </div>
                <Flex vertical align="center" justify="center" style={{ position: 'relative', height: '100%', color: 'white', textAlign: 'center' }}>
                    <BookTitle> { t('book.reserve-table') } </BookTitle>
                    <BookDescription> { t("book.description") } </BookDescription>
                </Flex>
            </div>

            {/* Reservation Form */}
            <Flex align="center" justify="center" style={{ backgroundColor: '#1F1F1F' }}>
                <ContainerBook>
                    <Flex vertical gap={10} style={{ backgroundColor: 'white', borderRadius: 10, padding: '20px 10px', marginTop: 50, width: '100%' }}>
                        <span style={{ fontSize: '3rem', fontFamily: 'Suburban' }}> { t('book.title') } </span>
                        <Divider />
                        <Form
                            form={form}
                            layout="vertical"
                            style={{ width: '100%' }}
                            preserve
                            onFinish={handleSubmit}
                            onValuesChange={handleValuesChange}
                        >
                            <Steps
                                size="small"
                                style={{ paddingInline: 30 }}
                                current={currentTab}
                                items={[
                                    { title: t('book.basic-info') },
                                    { title: t('book.special-req') },
                                    { title: t('book.confirmation') }
                                ]}
                            />
                            <div style={{ padding: 20 }}>
                                { currentTab === 0 &&
                                    <>
                                        <FormContainer>
                                            <Form.Item
                                                label={ t('label.guest-name') }
                                                name="sName"
                                                style={{ width: '100%' }}
                                                rules={[{ required: true, message: t('label.enter-name') }]}
                                            >
                                                <Input
                                                    className="input-book"
                                                    style={{ width: '100%', padding: '10px 20px', border: '1px solid lightgrey', borderRadius: 5 }}
                                                    placeholder={ t('label.placeholder-name') }
                                                />
                                            </Form.Item>

                                            <Form.Item
                                                label={ t('label.phone') }
                                                name="sPhone"
                                                style={{ width: '100%' }}
                                                rules={[
                                                    { required: true, message: t('label.enter-phone') },
                                                ]}
                                            >
                                                <Input
                                                    className="input-book"
                                                    style={{ width: '100%', padding: '10px 20px', border: '1px solid lightgrey', borderRadius: 5 }}
                                                    placeholder={ t('label.placeholder-phone') }
                                                />
                                            </Form.Item>

                                            <Flex vertical gap={5} style={{ width: '100%' }}>
                                                <Form.Item
                                                    label={ t('label.date') }
                                                    name="date"
                                                    style={{ width: '100%', margin: 0 }}
                                                    rules={[{ required: true, message: t('label.enter-date') }]}
                                                >
                                                    <DatePicker
                                                        className="input-book"
                                                        showNow={false}
                                                        style={{ width: '100%', padding: '10px 20px', border: '1px solid lightgrey', borderRadius: 5 }}
                                                        disabledDate={disabledDate}
                                                    />
                                                </Form.Item>
                                                <Tag style={{ marginTop: 5, marginBottom: 20 }}> { t('book.today-book') } <strong> { t('book.today')} </strong> { t('book.call-us') } </Tag>
                                            </Flex>

                                            <Form.Item
                                                label={ t('label.number-guests') }
                                                name="nGuests"
                                                style={{ width: '100%' }}
                                                rules={[
                                                    { required: true, message: t('label.enter-guests') },
                                                ]}
                                            >
                                                <InputNumber
                                                    min={1}
                                                    max={20}
                                                    className="input-book"
                                                    style={{ width: '100%', padding: '10px 20px', border: '1px solid lightgrey', borderRadius: 5 }}
                                                    placeholder={ t('label.placeholder-guests') }
                                                />
                                            </Form.Item>
                                            <Form.Item
                                                label={ t('label.time-slot') }
                                                name="nIDTimeSlot"
                                                style={{ width: '100%' }}
                                                rules={[
                                                    { required: true, message: t('label.select-time-slot') },
                                                ]}
                                            >
                                                <Radio.Group className="input-book" buttonStyle="solid" optionType="button" onChange={(e: any) => onTimeSlot(e.target.value)}>
                                                    { times?.data?.map((time: any) => ( <Radio key={time.nID} value={time.nID}> { `${time.tStart} - ${time.tEnd}` } </Radio> ))}
                                                </Radio.Group>
                                            </Form.Item>
                                            { timeSlot &&
                                                <Form.Item
                                                    label={ t('label.time') }
                                                    name="sTime"
                                                    style={{ width: '100%' }}
                                                    rules={[
                                                        { required: true, message: t('label.select-time') },
                                                    ]}
                                                >
                                                    <Radio.Group className="input-book" buttonStyle="solid" optionType="button" >
                                                        { timeSlot?.map((time: any) => ( <Radio key={time} value={time}> { time  } </Radio> ))}
                                                    </Radio.Group>
                                                </Form.Item>
                                            }
                                        </FormContainer>
                                        <Flex justify="flex-end" style={{ padding: '15px 20px' }}>
                                            <Button
                                                type="primary"
                                                htmlType="submit"
                                                style={{ borderRadius: 5, backgroundColor: 'rgb(13, 156, 232)', border: 'none', height: 35, fontSize: '0.8rem', cursor: 'pointer' }}
                                            >
                                                { t('buttons.next-step') }
                                            </Button>
                                        </Flex>
                                    </>
                                }
                                { currentTab === 1 &&
                                    <>
                                        { guestsNumber >= 16 && <Tag style={{ whiteSpace: 'pre-line', marginBottom: 10 }}> { t('label.menu-available') } <a href={`https://ams-carriot-restaurant.com/menu-group/?nIDRestaurant=${nIDRestaurant}`} target="_blank" rel="noopener noreferrer"> { t('label.menu-groups') } </a>. </Tag> }
                                        <FormContainer>
                                            <Form.Item
                                                label={ t('label.setting-preference') }
                                                name="sSetting"
                                                style={{ width: '100%' }}
                                            >
                                                <Select className="input-book" placeholder={ t('label.placeholder-seating-preference') } style={{ height: 50 }}>
                                                    <Option value="indoor"> { t('label.indoor') } </Option>
                                                    <Option value="outdoor"> { t('label.outdoor') } </Option>
                                                </Select>
                                            </Form.Item>

                                            { guestsNumber >= 16 &&
                                                <Form.Item
                                                    label={ t('label.choose') }
                                                    name="bMenu"
                                                    style={{ width: '100%' }}
                                                >
                                                    <Radio.Group className="input-book" optionType="button" buttonStyle="solid">
                                                        <Radio value={true}> { t('label.yes') } </Radio>
                                                        <Radio value={false}> { t('label.no') } </Radio>
                                                    </Radio.Group>
                                                </Form.Item>
                                            }
                                            { wantMenu &&
                                                <Form.Item
                                                    label={ t('label.menu-preference') }
                                                    name="nIDMenu"
                                                    style={{ width: '100%' }}
                                                >
                                                    <Select className="input-book" placeholder={ t('label.menu-preference-placeholder') } style={{ height: 50 }}>
                                                        { data?.data.map((menu: any) => <Option key={menu.nId} value={menu.nID}> { menu.languages['name-' + i18n.language] } </Option> ) }
                                                    </Select>
                                                </Form.Item>
                                            }

                                            <Form.Item name="sDescription" label={ t('label.special-req-dietary') } style={{ width: '100%' }}>
                                                <TextArea
                                                    className="input-book"
                                                    style={{ width: '100%', padding: '10px 20px', border: '1px solid lightgrey', borderRadius: 5 }}
                                                    autoSize
                                                    placeholder={ t('label.placeholder-alergies') }
                                                />
                                            </Form.Item>
                                        </FormContainer>
                                        <Flex justify="space-between" style={{ padding: '15px 20px' }}>
                                            <Button
                                                type="default"
                                                onClick={() => setCurrentTab(currentTab - 1)}
                                                style={{ borderRadius: 5, height: 35, fontSize: '0.8rem', cursor: 'pointer' }}
                                            >
                                                { t('buttons.previous-step') }
                                            </Button>
                                            <Button
                                                type="primary"
                                                htmlType="submit"
                                                style={{ borderRadius: 5, backgroundColor: 'rgb(13, 156, 232)', border: 'none', height: 35, fontSize: '0.8rem', cursor: 'pointer' }}
                                            >
                                                { t('buttons.next-step') }
                                            </Button>
                                        </Flex>
                                    </>
                                }
                                { currentTab === 2 &&
                                    <Flex vertical gap={20}>
                                        <span style={{ fontFamily: 'Roboto-Bold', fontSize: '1.2rem' }}> { t('book.confirmation') } </span>
                                        <Flex vertical gap={10}>
                                            <Row>
                                                <Col span={5}> { t('label.sName') } </Col>
                                                <Col> { reservation.sName } </Col>
                                            </Row>
                                            <Row>
                                                <Col span={5}> { t('label.dDate') } </Col>
                                                <Col> { dayjs(reservation.date).format("DD-MM-YYYY") } </Col>
                                            </Row>
                                            <Row>
                                                <Col span={5}> { t('label.nHour') } </Col>
                                                <Col> { reservation.sTime } </Col>
                                            </Row>
                                            <Row>
                                                <Col span={5}> { t('label.nGuests') } </Col>
                                                <Col> { reservation.nGuests } </Col>
                                            </Row>
                                        </Flex>
                                        <Form.Item
                                            name="bAcceptConditions"
                                            valuePropName="checked"
                                            rules={[
                                                {
                                                validator: (_, value) =>
                                                    value ? Promise.resolve() : Promise.reject(t('label.check-conditions')),
                                                },
                                            ]}
                                            style={{ width: '100%' }}
                                        >
                                            <Checkbox
                                                style={{ width: '100%', padding: '10px 20px', border: '1px solid lightgrey', borderRadius: 5 }}
                                            >
                                                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                                                { t('label.accept') } <a onClick={redirectToLegal}> { t('label.conditions-use') } </a> { t('label.and') } <a onClick={redirectToLegal}> { t('label.private-policy') } </a>.
                                            </Checkbox>
                                        </Form.Item>
                                        <Flex justify="space-between" style={{ padding: '15px 20px' }}>
                                            <Button
                                                type="default"
                                                onClick={() => setCurrentTab(currentTab - 1)}
                                                style={{ borderRadius: 5, height: 35, fontSize: '0.8rem', cursor: 'pointer' }}
                                            >
                                                { t('buttons.previous-step') }
                                            </Button>
                                            <Button
                                                type="primary"
                                                htmlType="submit"
                                                style={{ borderRadius: 5, backgroundColor: 'rgb(13, 156, 232)', border: 'none', height: 50, fontSize: '0.8rem', cursor: 'pointer' }}
                                            >
                                                { t('buttons.confirm-book') }
                                            </Button>
                                        </Flex>
                                    </Flex>
                                } 
                            </div>
                        </Form>
                    </Flex>
                    <Flex vertical gap={20} align="center" justify="center" style={{ width: '100%', marginTop: 50 }}>
                        <span style={{ fontFamily: 'Roboto-Thin', color: 'lightgrey', fontSize: '0.8rem'}}> { t('book.manage-booking') } </span>
                        <Button
                            onClick={() => navigate(`/agenda?nIDRestaurant=${nIDRestaurant}`)}
                            style={{ backgroundColor: 'rgb(56, 56, 56)', color: 'white', border: 'none' }}
                        >
                            { t('buttons.view-bookings') }
                        </Button>
                    </Flex>
                </ContainerBook>
            </Flex>
            <Footer nIDRestaurant={nIDRestaurant} />
            <Modal open={confirmation} footer={null} onCancel={() => setConfirmation(false)}>
                <Result
                    status="success"
                    title={ t('book.confirmation-title') }
                    subTitle={ t('book.confirmation-description') }
                    extra={[
                        <Button type="primary" key="ok" onClick={onClose}>
                            { t('buttons.ok') }
                        </Button>
                    ]}
                />
            </Modal>
        </div>
    );
};

export default Book;
